import React, { forwardRef } from "react";
import { Link } from "react-router-dom"; // Optional
import { CgSpinner } from "react-icons/cg";
import { HiOutlineArrowRight } from "react-icons/hi";
import { Tooltip } from "flowbite-react";

const variantStyles = {
  default: "bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white hover:from-[#3B82F6] hover:to-[#1E3A8A] border-0 px-2",
  danger: "bg-reject text-white hover:bg-reject border-0 px-2",
  success: "bg-approved text-white hover:bg-approved border-0 px-2",
  primary: "px-2 bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white hover:from-[#1E3A8A] hover:to-[#3B82F6] border-0",
  secondary:
    "px-2 bg-light text-theme hover:bg-bghoverlight border border-borderColor",
  outline:
    "px-2 bg-white hover:bg-bghoverlight text-theme border border-borderColor",
  icon: "bg-light text-theme hover:bg-bghoverlight hover:text-dark",
  disabled: "px-2 bg-disabled/20 text-disabled cursor-not-allowed",

  // warning / info based
  warning: "bg-yellow-500 text-white hover:bg-yellow-600 border-0",
  info: "bg-blue-500 text-white hover:bg-blue-600 border-0",

  // 🔹 Contextual actions
  action: "bg-orange-500 text-white hover:bg-orange-600 border-0", //Update Status orange color
  utility: "bg-teal-500 text-white hover:bg-teal-600 border-0", //send Email teal color

  // 🔹 Extended styles Black and white
  dark: "bg-gray-800 text-white hover:bg-gray-900 border-0",
  light: "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300",
  gradient:
    "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white border-0", // chatbot color indigo+purple+pink+white

  // 🔹 Outline versions
  successOutline:
    "bg-white text-approved border border-approved hover:bg-approved hover:text-white",
  dangerOutline:
    "bg-white text-reject border border-reject hover:bg-reject hover:text-white",
  warningOutline:
    "bg-white text-yellow-500 border border-yellow-500 hover:bg-yellow-500 hover:text-white",
  infoOutline:
    "bg-white text-blue-500 border border-blue-500 hover:bg-blue-500 hover:text-white",
  actionOutline:
    "bg-white text-orange-500 border border-orange-500 hover:bg-orange-500 hover:text-white",
  utilityOutline:
    "bg-white text-teal-500 border border-teal-500 hover:bg-teal-500 hover:text-white",
};

const sizeStyles = {
  default: "min-h-7 min-w-7 py-1.5 text-sm",
  full: "w-full h-10 text-sm",
  sm: "h-8 px-2 text-sm",
  md: "h-10 px-3 text-base",
  lg: "h-12 px-4 text-lg",
  w_lg: "min-w-28 px-4 py-2 text-sm",
  icon: "h-7 w-7 text-md p-0",
};

export const DefaultButton = forwardRef(
  (
    {
      name,
      icon,
      rightIcon,
      iconClass = "",
      classnames = "",
      variant = "default",
      size = "default",
      type = "button",
      disabled = false,
      loading = false,
      tooltip = "",
      minWidth = false,
      onClick,
      link,
      ...rest // pass interaction props to the button (e.g., from floating-ui)
    },
    ref
  ) => {
    const isIconOnly = icon && !name;
    const computedVariant = isIconOnly ? "icon" : variant;
    const isDisabled = disabled || loading;

    const baseClass =
      `${variantStyles[isDisabled ? "disabled" : computedVariant]} ` +
      `${sizeStyles[isIconOnly ? "icon" : size]} ` +
      `flex items-center justify-center gap-1.5 rounded-full transition duration-200 group ` +
      `${isDisabled ? "pointer-events-none opacity-50" : ""} ` +
      `${minWidth ? "min-w-[100px]" : ""} ` +
      `max-w-full truncate overflow-hidden text-ellipsis ` +
      `${classnames}`;

    const showArrow = rightIcon === true || (rightIcon && typeof rightIcon === "object");
    const arrowContent = showArrow ? (rightIcon === true ? <HiOutlineArrowRight className="w-5 h-5" /> : rightIcon) : null;
    const content = (
      <>
        {loading ? (
          <CgSpinner className="animate-spin text-lg" />
        ) : (
          <>
            {icon && <span className={`text-lg ${iconClass}`}>{icon}</span>}
            {name && <span className="capitalize truncate">{name}</span>}
            {arrowContent && (
              <span
                className="inline-flex flex-shrink-0 transition-all duration-300 ease-out opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0"
                aria-hidden
              >
                {arrowContent}
              </span>
            )}
          </>
        )}
      </>
    );

    const renderButton = () => (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        onClick={onClick}
        className={baseClass}
        {...rest}
      >
        {content}
      </button>
    );

    const renderLink = () =>
      typeof link === "string" ? (
        <a
          href={link}
          className={baseClass}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => isDisabled && e.preventDefault()}
        >
          {content}
        </a>
      ) : (
        <Link
          to={link}
          className={baseClass}
          onClick={(e) => isDisabled && e.preventDefault()}
        >
          {content}
        </Link>
      );

    return tooltip ? (
      <Tooltip className="text-xs shadow-md" content={tooltip}>
        {link ? renderLink() : renderButton()}
      </Tooltip>
    ) : link ? (
      renderLink()
    ) : (
      renderButton()
    );
  }
);

export default DefaultButton;
